import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Checkbox,
  Tabs,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import {
  dispatchService,
  CreateDispatchData,
  DispatchCheckin,
  DispatchCheckout,
  DispatchCollect,
  DispatchReturn,
  DispatchComment,
  DispatchReview,
  dispatchCommentService,
} from '../services/dispatches';
import { customerService } from '../services/customers';
import { businessService } from '../services/businesses';
import { taskTypeService } from '../services/taskTypes';
import { taskTemplateService } from '../services/taskTemplates';
import { staffService, Staff } from '../services/staffs';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd';
import api from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

export const DispatchFormPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'dispatches.update');
  const canDelete = hasPermission(user, 'dispatches.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingDispatch, setLoadingDispatch] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const [customers, setCustomers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [customerType, setCustomerType] = useState<'personal' | 'business'>('personal');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [taskTypes, setTaskTypes] = useState<any[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  const [users, setUsers] = useState<Staff[]>([]);
  const [checkins, setCheckins] = useState<DispatchCheckin[]>([]);
  const [checkouts, setCheckouts] = useState<DispatchCheckout[]>([]);
  const [collects, setCollects] = useState<DispatchCollect[]>([]);
  const [returns, setReturns] = useState<DispatchReturn[]>([]);
  const [collectUploads, setCollectUploads] = useState<Record<string, File | null>>({});
  const [returnUploads, setReturnUploads] = useState<Record<string, File | null>>({});
  const [comments, setComments] = useState<DispatchComment[]>([]);
  const [reviews, setReviews] = useState<DispatchReview[]>([]);
  const [commentText, setCommentText] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [dispatchName, setDispatchName] = useState<string>('');
  const [isEditing, setIsEditing] = useState(!isEdit); // For new dispatches, default to editing mode
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadDispatch(parseInt(id, 10));
    } else {
      // Set default status for new dispatch
      form.setFieldsValue({ status: 'Pending' });
    }
  }, [isEdit, id]);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'status-logs') {
      setActiveTab('overview');
      setSearchParams({ tab: 'overview' });
    }
  }, [activeTab, setSearchParams]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const fetchInitialData = async () => {
    try {
      const [customersResponse, businessesResponse, taskTypesResponse, taskTemplatesResponse, staffResponse] = await Promise.all([
        customerService.getAll(),
        businessService.getAll().catch(() => ({ data: [] })),
        taskTypeService.getAll(),
        taskTemplateService.getAll(),
        staffService.getAll().catch(() => []),
      ]);
      setCustomers(customersResponse.data || customersResponse || []);
      setBusinesses(businessesResponse.data || []);
      setTaskTypes(taskTypesResponse.data || taskTypesResponse || []);
      setTaskTemplates(taskTemplatesResponse.data || taskTemplatesResponse || []);
      setUsers(Array.isArray(staffResponse) ? staffResponse : []);
    } catch {
      message.error('Failed to load initial data');
    }
  };

  const loadDispatch = async (dispatchId: number) => {
    setLoadingDispatch(true);
    try {
      const dispatch = await dispatchService.getById(dispatchId);
      const resolvedType = (dispatch as any).type ?? ((dispatch as any).business_id ? 'business' : 'personal');
      const resolvedReferenceId = (dispatch as any).reference_id ?? ((dispatch as any).business_id ?? dispatch.customer_id);
      
      form.setFieldsValue({
        customer_id: dispatch.customer_id,
        task_type_id: dispatch.task_type_id,
        year: dispatch.year,
        task_template_id: dispatch.task_id, // Note: dispatch uses task_id but we'll map it to task_template_id for the form
        create_date: dispatch.create_date ? dayjs(dispatch.create_date) : null,
        collect_date: dispatch.collect_date ? dayjs(dispatch.collect_date) : null,
        collect_due: dispatch.collect_due ? dayjs(dispatch.collect_due) : null,
        return_date: dispatch.return_date ? dayjs(dispatch.return_date) : null,
        return_due: dispatch.return_due ? dayjs(dispatch.return_due) : null,
        location: dispatch.location,
        agent_id: dispatch.agent_id,
        status: dispatch.status || 'Pending',
        type: resolvedType,
        reference_id: resolvedReferenceId,
      });
      
      setCustomerType(resolvedType);
      // Handle business_id
      if (resolvedType === 'business' && resolvedReferenceId) {
        form.setFieldsValue({ business_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        let business = businesses.find(b => b.id === resolvedReferenceId)
          || businesses.find(b => b.id === (dispatch as any).business_id);
        if (!business) {
          try {
            business = await businessService.getById(resolvedReferenceId);
          } catch {
            business = undefined;
          }
        }
        if (business) {
          setSelectedBusiness(business);
          if (business.customer_id) {
            try {
              const customer = await customerService.getById(business.customer_id);
              setSelectedCustomer(customer);
              form.setFieldsValue({ customer_id: business.customer_id });
            } catch {
              // Ignore error
            }
          }
        }
      } else if (resolvedType === 'personal' && resolvedReferenceId) {
        form.setFieldsValue({ customer_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        try {
          const customer = await customerService.getById(resolvedReferenceId);
          setSelectedCustomer(customer);
        } catch {
          // Ignore error
        }
      }
      
      setCheckins(dispatch.checkins || []);
      setCheckouts(dispatch.checkouts || []);
      setCollects(dispatch.collects || []);
      setReturns(dispatch.returns || []);
      setCollectUploads({});
      setReturnUploads({});
      setComments(dispatch.comments || []);
      setReviews(dispatch.reviews || []);
      
      // Set dispatch name immediately after loading
      setDispatchName(formatDispatchNumber(dispatchId));
    } catch {
      message.error('Failed to load dispatch');
    } finally {
      setLoadingDispatch(false);
    }
  };

  const handleCustomerTypeChange = (type: 'personal' | 'business') => {
    setCustomerType(type);
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    form.setFieldsValue({
      type,
      reference_id: undefined,
      customer_id: undefined,
      business_id: undefined,
      agent_id: undefined,
    });
  };

  const handleBusinessChange = async (businessId: number) => {
    if (businessId) {
      try {
        const business = await businessService.getById(businessId);
        setSelectedBusiness(business);
        if (business.customer_id) {
          const customer = await customerService.getById(business.customer_id);
          setSelectedCustomer(customer);
          form.setFieldsValue({
            customer_id: business.customer_id,
            reference_id: businessId,
            agent_id: customer.agent_id || business.agent_id || undefined,
          });
        } else {
          form.setFieldsValue({
            customer_id: undefined,
            reference_id: businessId,
            agent_id: business.agent_id || undefined,
          });
        }
      } catch {
        message.error('Failed to load business details');
      }
    } else {
      setSelectedBusiness(null);
      setSelectedCustomer(null);
      form.setFieldsValue({
        customer_id: undefined,
        reference_id: undefined,
        agent_id: undefined,
      });
    }
  };

  const handleCustomerChange = async (customerId: number) => {
    if (customerId) {
      try {
        const customer = await customerService.getById(customerId);
        setSelectedCustomer(customer);
        form.setFieldsValue({ agent_id: customer.agent_id, reference_id: customerId });
      } catch {
        message.error('Failed to load customer');
      }
    } else {
      setSelectedCustomer(null);
      form.setFieldsValue({ agent_id: null, reference_id: undefined });
    }
  };

  const getCollectKey = (collect: DispatchCollect, index: number) => {
    return String(collect.id ?? `collect-${index}`);
  };

  const getReturnKey = (returnItem: DispatchReturn, index: number) => {
    return String(returnItem.id ?? `return-${index}`);
  };

  const getAttachmentName = (path?: string | null) => {
    if (!path) return 'attachment';
    const parts = path.split('/');
    return parts[parts.length - 1] || 'attachment';
  };

  const handleViewAttachment = async (
    type: 'collect' | 'return',
    itemId?: number,
    file?: UploadFile
  ) => {
    try {
      if (file?.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const newWindow = window.open(blobUrl, '_blank');
        if (newWindow) {
          newWindow.addEventListener('beforeunload', () => {
            URL.revokeObjectURL(blobUrl);
          });
        }
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 60000);
        return;
      }

      if (!id || !itemId) return;
      const url =
        type === 'collect'
          ? `/dispatches/${id}/collects/${itemId}/attachment/view`
          : `/dispatches/${id}/returns/${itemId}/attachment/view`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank');
      if (newWindow) {
        newWindow.addEventListener('beforeunload', () => {
          URL.revokeObjectURL(blobUrl);
        });
      }
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch {
      message.error('Failed to view attachment');
    }
  };

  const handleDownloadAttachment = async (
    type: 'collect' | 'return',
    itemId?: number,
    file?: UploadFile
  ) => {
    try {
      if (file?.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.name || 'attachment';
        link.click();
        URL.revokeObjectURL(blobUrl);
        return;
      }

      if (!id || !itemId) return;
      const url =
        type === 'collect'
          ? `/dispatches/${id}/collects/${itemId}/attachment/download`
          : `/dispatches/${id}/returns/${itemId}/attachment/download`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'attachment';
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      message.error('Failed to download attachment');
    }
  };

  const handleTaskTemplateChange = async (templateId?: number) => {
    if (!templateId) {
      setCheckins([]);
      setCheckouts([]);
      return;
    }
    try {
      const response = await api.get('/sub-tasks');
      const allSubTasks = response.data?.data || response.data || [];
      const subTasks = allSubTasks.filter((st: any) => st.task_template_id === templateId);
      const newCheckins = subTasks.map((st: any, index: number) => ({
        id: `temp-checkin-${Date.now()}-${index}`,
        dispatch_id: id ? parseInt(id, 10) : 0,
        name: st.name || '',
        enabled: true,
        created_at: new Date().toISOString(),
      }));
      setCheckins(newCheckins);
      setCheckouts([]);
    } catch {
      message.error('Failed to load checklist');
    }
  };

  const handleAddCheckin = () => {
    const newCheckin: DispatchCheckin = {
      id: `temp-checkin-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      enabled: true,
      created_at: new Date().toISOString(),
    };
    setCheckins(prev => [...prev, newCheckin]);
  };

  const handleDeleteCheckin = (checkinId: number | string) => {
    setCheckins(prev => prev.filter(c => c.id !== checkinId));
  };

  const handleAddCheckout = () => {
    const newCheckout: DispatchCheckout = {
      id: `temp-checkout-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      enabled: true,
      created_at: new Date().toISOString(),
    };
    setCheckouts(prev => [...prev, newCheckout]);
  };

  const handleDeleteCheckout = (checkoutId: number | string) => {
    setCheckouts(prev => prev.filter(c => c.id !== checkoutId));
  };

  const handleAddCollect = () => {
    const newCollect: DispatchCollect = {
      id: `temp-collect-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      attachment: null,
      enabled: true,
      created_at: new Date().toISOString(),
    };
    setCollects(prev => [...prev, newCollect]);
  };

  const handleDeleteCollect = (collectId: number | string) => {
    setCollects(prev => prev.filter(c => c.id !== collectId));
    setCollectUploads(prev => {
      const next = { ...prev };
      delete next[String(collectId)];
      return next;
    });
  };

  const handleAddReturn = () => {
    const newReturn: DispatchReturn = {
      id: `temp-return-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      name: '',
      attachment: null,
      enabled: true,
      created_at: new Date().toISOString(),
    };
    setReturns(prev => [...prev, newReturn]);
  };

  const handleDeleteReturn = (returnId: number | string) => {
    setReturns(prev => prev.filter(r => r.id !== returnId));
    setReturnUploads(prev => {
      const next = { ...prev };
      delete next[String(returnId)];
      return next;
    });
  };

  const handleAddComment = () => {
    if (!commentText.trim()) {
      message.error('Please enter a comment');
      return;
    }
    const newComment: DispatchComment = {
      id: `temp-comment-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      comment: commentText,
      created_at: new Date().toISOString(),
      user: { id: user?.id || 0, name: user?.name || 'Current User', email: user?.email || '' },
    };
    setComments(prev => [...prev, newComment]);
    setCommentText('');
    message.success('Comment added (will be saved on submit)');
  };

  const handleDeleteComment = async (commentId: number | string) => {
    if (typeof commentId === 'number') {
      try {
        await dispatchCommentService.delete(commentId);
        setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
        message.success('Comment deleted');
        return;
      } catch {
        message.error('Failed to delete comment');
        if (id) {
          loadDispatch(parseInt(id, 10));
        }
        return;
      }
    }
    setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
  };

  const handleAddReview = () => {
    if (!reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }
    const newReview: DispatchReview = {
      id: `temp-review-${Date.now()}`,
      dispatch_id: id ? parseInt(id, 10) : 0,
      customer_id: form.getFieldValue('customer_id'),
      review: reviewText,
      created_at: new Date().toISOString(),
      creator: { id: user?.id || 0, name: user?.name || 'Current User', email: user?.email || '' },
    };
    setReviews(prev => [...prev, newReview]);
    setReviewText('');
    message.success('Review added (will be saved on submit)');
  };

  const handleDeleteReview = (reviewId: number | string) => {
    setReviews(prev => prev.filter(r => r.id !== reviewId));
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const getDisplayValue = (fieldName: string, type: 'select' | 'date' | 'number' | 'text' = 'text', options?: any[]) => {
    const value = form.getFieldValue(fieldName);
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value);
      return option?.name || '-';
    }
    
    if (type === 'date') {
      if (!value) return '-';
      if (dayjs.isDayjs(value)) {
        return value.format('DD/MM/YYYY');
      }
      if (typeof value === 'string') {
        return dayjs(value).format('DD/MM/YYYY');
      }
      return '-';
    }
    
    if (type === 'number') {
      return value || '-';
    }
    
    return value || '-';
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // Remove fields that don't exist in dispatches table
      const { sender_id, team_id, staff_id, ...cleanValues } = values;

      const checkinPayload = checkins.map(c => ({
        id: typeof c.id === 'number' ? c.id : undefined,
        name: c.name,
        enabled: c.enabled,
      }));
      const checkoutPayload = checkouts.map(c => ({
        id: typeof c.id === 'number' ? c.id : undefined,
        name: c.name,
        enabled: c.enabled,
      }));
      const collectPayload = collects.map((c, index) => {
        const key = getCollectKey(c, index);
        const file = collectUploads[key];
        return {
          id: typeof c.id === 'number' ? c.id : undefined,
          name: c.name || file?.name || '',
          enabled: c.enabled,
          attachment: file ? undefined : c.attachment,
        };
      });
      const returnPayload = returns.map((r, index) => {
        const key = getReturnKey(r, index);
        const file = returnUploads[key];
        return {
          id: typeof r.id === 'number' ? r.id : undefined,
          name: r.name || file?.name || '',
          enabled: r.enabled,
          attachment: file ? undefined : r.attachment,
        };
      });
      const commentPayload = comments
        .filter(c => typeof c.id === 'string' && String(c.id).startsWith('temp-'))
        .map(c => ({ comment: c.comment }));
      const reviewPayload = reviews
        .filter(r => typeof r.id === 'string' && String(r.id).startsWith('temp-'))
        .map(r => ({ customer_id: r.customer_id, review: r.review }));

      const referenceId = customerType === 'business' ? values.business_id : values.customer_id;
      const payload: CreateDispatchData = {
        ...cleanValues,
        task_id: values.task_template_id,
        type: customerType,
        reference_id: referenceId,
        customer_id: values.business_id ? selectedBusiness?.customer_id : values.customer_id,
        business_id: values.business_id || undefined,
        status: values.status || form.getFieldValue('status') || 'Pending',
        create_date: values.create_date?.format('YYYY-MM-DD'),
        collect_date: values.collect_date?.format('YYYY-MM-DD'),
        collect_due: values.collect_due?.format('YYYY-MM-DD'),
        return_date: values.return_date?.format('YYYY-MM-DD'),
        return_due: values.return_due?.format('YYYY-MM-DD'),
        checkins: checkinPayload,
        checkouts: checkoutPayload,
        collects: collectPayload,
        returns: returnPayload,
        comments: commentPayload,
        reviews: reviewPayload,
      };

      const hasCollectFiles = Object.values(collectUploads).some(file => file instanceof File);
      const hasReturnFiles = Object.values(returnUploads).some(file => file instanceof File);
      const hasFiles = hasCollectFiles || hasReturnFiles;
      let submitData: CreateDispatchData | FormData = payload;

      if (hasFiles) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }
          if (Array.isArray(value)) {
            return;
          }
          formData.append(key, value.toString());
        });

        checkinPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null) {
              formData.append(`checkins[${index}][${itemKey}]`, itemValue.toString());
            }
          });
        });
        checkoutPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null) {
              formData.append(`checkouts[${index}][${itemKey}]`, itemValue.toString());
            }
          });
        });
        collectPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null && itemKey !== 'attachment') {
              formData.append(`collects[${index}][${itemKey}]`, itemValue.toString());
            }
          });
          const key = getCollectKey(collects[index], index);
          const file = collectUploads[key];
          if (file) {
            formData.append(`collects[${index}][attachment]`, file);
          } else if (item.attachment !== undefined) {
            formData.append(`collects[${index}][attachment]`, item.attachment ?? '');
          }
        });
        returnPayload.forEach((item, index) => {
          Object.entries(item).forEach(([itemKey, itemValue]) => {
            if (itemValue !== undefined && itemValue !== null && itemKey !== 'attachment') {
              formData.append(`returns[${index}][${itemKey}]`, itemValue.toString());
            }
          });
          const key = getReturnKey(returns[index], index);
          const file = returnUploads[key];
          if (file) {
            formData.append(`returns[${index}][attachment]`, file);
          } else if (item.attachment !== undefined) {
            formData.append(`returns[${index}][attachment]`, item.attachment ?? '');
          }
        });
        commentPayload.forEach((item, index) => {
          formData.append(`comments[${index}][comment]`, item.comment);
        });
        reviewPayload.forEach((item, index) => {
          if (item.customer_id !== undefined && item.customer_id !== null) {
            formData.append(`reviews[${index}][customer_id]`, item.customer_id.toString());
          }
          formData.append(`reviews[${index}][review]`, item.review);
        });

        submitData = formData;
      }

      if (isEdit && id) {
        await dispatchService.update(parseInt(id, 10), submitData);
        message.success('Dispatch updated successfully');
      } else {
        await dispatchService.create(submitData);
        message.success('Dispatch created successfully');
      }

      navigate('/dispatches');
    } catch (error: any) {
      console.error('Failed to save dispatch:', error);
      message.error(isEdit ? 'Failed to update dispatch' : 'Failed to create dispatch');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDispatchNumber = (dispatchId: number) => {
    return `#${dispatchId.toString().padStart(7, '0')}`;
  };

  const getDispatchDisplayName = () => {
    if (isEdit && id) {
      return formatDispatchNumber(parseInt(id, 10));
    }
    return 'New Dispatch';
  };

  useEffect(() => {
    if (isEdit && id && customers.length > 0 && taskTypes.length > 0) {
      const displayName = getDispatchDisplayName();
      setDispatchName(displayName);
    }
  }, [isEdit, id, customers, taskTypes, form.getFieldValue('customer_id'), form.getFieldValue('task_type_id')]);

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this dispatch?')) return;
    
    try {
      await dispatchService.delete(parseInt(id, 10));
                      message.success('Dispatch deleted');
                      navigate('/dispatches');
    } catch {
      message.error('Failed to delete dispatch');
                  }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {/* Header */}
      <div style={{
        marginBottom: 24,
        paddingBottom: 16,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {isEdit ? (dispatchName || 'Loading...') : 'New Dispatch'}
          </Title>
          <Space>
            {isEdit && !isEditing && canUpdate && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {isEdit && canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loadingDispatch || (isEdit && !isEditing)}
        onValuesChange={(changedValues) => {
          if (changedValues.customer_id !== undefined || changedValues.task_type_id !== undefined) {
            const displayName = getDispatchDisplayName();
            setDispatchName(displayName);
          }
        }}
      >
        <Form.Item name="type" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="reference_id" hidden>
          <Input />
        </Form.Item>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status} noStyle>
              {({ getFieldValue }) => {
                const currentStatus = getFieldValue('status') || 'Pending';
                const mapStatus = (status: string | undefined): 'Pending' | 'Collect' | 'Return' => {
                  if (!status) return 'Pending';
                  const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                  if (['Pending', 'Collect', 'Return'].includes(normalized)) {
                    return normalized as 'Pending' | 'Collect' | 'Return';
                  }
                  return 'Pending';
                };
                const mappedStatus = mapStatus(currentStatus);
                
                const statusConfig = {
                  Pending: { bg: '#E6F7FF', border: '#91D5FF', color: '#1890ff', dot: '#1890ff', label: 'Pending' },
                  Collect: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Collect' },
                  Return: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Return' },
                };
                
                const statuses: Array<'Pending' | 'Collect' | 'Return'> = ['Pending', 'Collect', 'Return'];
                
                return (
                  <Space size={8}>
                    {statuses.map((status) => {
                      const isActive = mappedStatus === status;
                      const config = statusConfig[status];
                      return (
                        <div
                          key={status}
                          onClick={() => {
                            if (isEditing) {
                              form.setFieldsValue({ status });
                            }
                          }}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: isActive ? config.bg : '#f5f5f5',
                            border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                            color: isActive ? config.color : '#595959',
                            fontWeight: isActive ? 500 : 400,
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s',
                            cursor: isEditing ? 'pointer' : 'default',
                          }}
                        >
                          <span style={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: isActive ? config.dot : '#8c8c8c',
                          }}></span>
                          {config.label}
                        </div>
                      );
                    })}
                  </Space>
                );
              }}
            </Form.Item>
          }
        >
          {/* Hidden Form.Item to bind status field */}
          <Form.Item name="status" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="agent_id" hidden>
            <Input type="hidden" />
          </Form.Item>
          {/* Overview Tab */}
          <TabPane tab="Overview" key="overview">
            <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Customer Type - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Customer</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Space>
                          <Button
                            type={customerType === 'personal' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('personal')}
                            size="small"
                          >
                            Personal
                          </Button>
                          <Button
                            type={customerType === 'business' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('business')}
                            size="small"
                          >
                            Business
                          </Button>
                        </Space>
                      ) : (
                        <div>
                          {customerType === 'personal' ? 'Personal' : 'Business'}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Customer/Business - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>{customerType === 'personal' ? 'Customer' : 'Business'}</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        customerType === 'personal' ? (
                          <Form.Item
                            name="customer_id"
                            rules={[{ required: true, message: 'Please select customer' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Customer"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleCustomerChange}
                            >
                              {customers.map((customer) => (
                                <Select.Option key={customer.id} value={customer.id}>
                                  {customer.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="business_id"
                            rules={[{ required: true, message: 'Please select business' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Business"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleBusinessChange}
                            >
                              {businesses.map((business) => (
                                <Select.Option key={business.id} value={business.id}>
                                  {business.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )
                      ) : (
                        <div>
                          {customerType === 'personal' 
                            ? getDisplayValue('customer_id', 'select', customers)
                            : getDisplayValue('business_id', 'select', businesses)
                          }
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Type 和 Year - 同一行，各占两列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Type</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="task_type_id" style={{ margin: 0 }}>
                          <Select placeholder="Select Type" allowClear>
                            {taskTypes.map((type) => (
                              <Select.Option key={type.id} value={type.id}>
                                {type.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('task_type_id', 'select', taskTypes)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Year</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="year" style={{ margin: 0 }}>
                          <Select placeholder="Select Year" allowClear>
                            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                              <Select.Option key={year} value={year}>
                                {year}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('year', 'number')}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Task - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Task</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="task_template_id" style={{ margin: 0 }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.task_type_id !== currentValues.task_type_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const taskTypeId = getFieldValue('task_type_id');
                              
                              // Filter task templates based on task_type_id
                              const filteredTemplates = taskTemplates.filter((template) => {
                                if (taskTypeId && template.task_type_id !== taskTypeId) return false;
                                return true;
                              });
                              
                              return (
                                <Select
                                  placeholder="Select Task"
                                  allowClear
                                  onChange={(value) => handleTaskTemplateChange(value)}
                                >
                                  {filteredTemplates.map((template) => (
                                    <Select.Option key={template.id} value={template.id}>
                                      {template.name}
                                    </Select.Option>
                                  ))}
                                </Select>
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('task_template_id', 'select', taskTemplates)}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Create - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Create</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="create_date" style={{ margin: 0 }}>
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Create Date" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('create_date', 'date')}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Collect Date 和 Collect Due - 同一行，各占两列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Collect Date</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="collect_date" style={{ margin: 0 }}>
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Collect Date" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('collect_date', 'date')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Collect Due</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="collect_due" style={{ margin: 0 }}>
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Collect Due Date" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('collect_due', 'date')}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Return Date 和 Return Due - 同一行，各占两列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Return Date</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="return_date" style={{ margin: 0 }}>
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Return Date" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('return_date', 'date')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Return Due</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="return_due" style={{ margin: 0 }}>
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Enter Return Due Date" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('return_due', 'date')}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Location - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Location</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="location" style={{ margin: 0 }}>
                          <Input placeholder="Enter Location" />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('location', 'text')}
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Advisor - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Advisor</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <div>
                        {selectedCustomer?.agent?.name || getDisplayValue('agent_id', 'select', users)}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </TabPane>

          {/* Checklist Tab */}
          <TabPane tab="Checklist" key="checklist">
            <Row gutter={24}>
              <Col span={12}>
                {/* Check In List */}
            <Card
                  title="Check In List"
                  bodyStyle={{ padding: '16px' }}
              extra={
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                      onClick={handleAddCheckin}
                >
                  Add
                </Button>
              }
            >
                  {checkins.map((checkin, index) => (
                    <div key={checkin.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Checkbox checked={checkin.enabled} onChange={(e) => {
                        setCheckins(prev => prev.map(c => c.id === checkin.id ? { ...c, enabled: e.target.checked } : c));
                      }} />
                  <Input
                        value={checkin.name || ''}
                    onChange={(e) => {
                          setCheckins(prev => prev.map(c => c.id === checkin.id ? { ...c, name: e.target.value } : c));
                    }}
                    style={{ flex: 1 }}
                    placeholder="Enter name"
                  />
                  <span style={{ color: '#999', marginRight: 8 }}>By</span>
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                        onClick={() => handleDeleteCheckin(checkin.id!)}
                              />
                            </div>
                          ))}
                  {checkins.length === 0 && (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No check ins. Click 'Add' to add a check in.
                            </div>
                          )}
            </Card>
              </Col>
              <Col span={12}>
                {/* Check Out List */}
            <Card
                  title="Check Out List"
                  bodyStyle={{ padding: '16px' }}
              extra={
                          <Button
                  type="link"
                            icon={<PlusOutlined />}
                      onClick={handleAddCheckout}
                          >
                  Add
                          </Button>
              }
            >
                  {checkouts.map((checkout, index) => (
                    <div key={checkout.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Checkbox checked={checkout.enabled} onChange={(e) => {
                        setCheckouts(prev => prev.map(c => c.id === checkout.id ? { ...c, enabled: e.target.checked } : c));
                      }} />
                  <Input
                        value={checkout.name || ''}
                    onChange={(e) => {
                          setCheckouts(prev => prev.map(c => c.id === checkout.id ? { ...c, name: e.target.value } : c));
                    }}
                    style={{ flex: 1 }}
                    placeholder="Enter name"
                  />
                  <span style={{ color: '#999', marginRight: 8 }}>By</span>
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                        onClick={() => handleDeleteCheckout(checkout.id!)}
                              />
                            </div>
                          ))}
                  {checkouts.length === 0 && (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No check outs. Click 'Add' to add a check out.
                            </div>
                          )}
            </Card>
          </Col>
        </Row>
          </TabPane>

          {/* Photo Tab */}
          <TabPane tab="Photo" key="photo">
            <Row gutter={24}>
              <Col span={12}>
            {/* Collect */}
            <Card
              title="Collect"
              bodyStyle={{ padding: '16px' }}
              extra={
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={handleAddCollect}
                >
                  Add
                </Button>
              }
            >
              {collects.map((collect, index) => {
                const key = getCollectKey(collect, index);
                const file = collectUploads[key];
                const existingAttachment = collect.attachment;
                const fileList: UploadFile[] = file
                  ? [{
                      uid: `${key}-upload`,
                      name: file.name,
                      status: 'done',
                      originFileObj: file as any,
                    }]
                  : existingAttachment
                    ? [{
                        uid: `${key}-existing`,
                        name: getAttachmentName(existingAttachment),
                        status: 'done',
                      }]
                    : [];

                return (
                  <div key={collect.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Upload
                      beforeUpload={() => false}
                      maxCount={1}
                      fileList={fileList}
                      disabled={!isEditing}
                      showUploadList={{ showRemoveIcon: isEditing }}
                      onChange={({ fileList: nextList }) => {
                        const nextFile = nextList[0]?.originFileObj as File | undefined;
                        setCollectUploads(prev => ({
                          ...prev,
                          [key]: nextFile || null,
                        }));
                        if (nextFile) {
                          setCollects(prev => prev.map(c => c.id === collect.id ? { ...c, attachment: null, name: c.name || nextFile.name } : c));
                        }
                      }}
                      onRemove={() => {
                        setCollectUploads(prev => ({ ...prev, [key]: null }));
                        setCollects(prev => prev.map(c => c.id === collect.id ? { ...c, attachment: null } : c));
                      }}
                    >
                      {isEditing ? (
                        <Button icon={<UploadOutlined />}>Upload Photo</Button>
                      ) : null}
                    </Upload>
                    {fileList.length > 0 && (
                      <Space size={4}>
                        <Button
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => handleViewAttachment('collect', typeof collect.id === 'number' ? collect.id : undefined, fileList[0])}
                        />
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadAttachment('collect', typeof collect.id === 'number' ? collect.id : undefined, fileList[0])}
                        />
                      </Space>
                    )}
                    {isEditing && (
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteCollect(collect.id!)}
                      />
                    )}
                  </div>
                );
              })}
              {collects.length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No collects. Click 'Add' to add a collect.
                </div>
              )}
            </Card>
              </Col>
              <Col span={12}>
            {/* Return */}
            <Card
              title="Return"
              bodyStyle={{ padding: '16px' }}
              extra={
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={handleAddReturn}
                >
                  Add
                </Button>
              }
            >
              {returns.map((returnItem, index) => {
                const key = getReturnKey(returnItem, index);
                const file = returnUploads[key];
                const existingAttachment = returnItem.attachment;
                const fileList: UploadFile[] = file
                  ? [{
                      uid: `${key}-upload`,
                      name: file.name,
                      status: 'done',
                      originFileObj: file as any,
                    }]
                  : existingAttachment
                    ? [{
                        uid: `${key}-existing`,
                        name: getAttachmentName(existingAttachment),
                        status: 'done',
                      }]
                    : [];

                return (
                  <div key={returnItem.id || index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Upload
                      beforeUpload={() => false}
                      maxCount={1}
                      fileList={fileList}
                      disabled={!isEditing}
                      showUploadList={{ showRemoveIcon: isEditing }}
                      onChange={({ fileList: nextList }) => {
                        const nextFile = nextList[0]?.originFileObj as File | undefined;
                        setReturnUploads(prev => ({
                          ...prev,
                          [key]: nextFile || null,
                        }));
                        if (nextFile) {
                          setReturns(prev => prev.map(r => r.id === returnItem.id ? { ...r, attachment: null, name: r.name || nextFile.name } : r));
                        }
                      }}
                      onRemove={() => {
                        setReturnUploads(prev => ({ ...prev, [key]: null }));
                        setReturns(prev => prev.map(r => r.id === returnItem.id ? { ...r, attachment: null } : r));
                      }}
                    >
                      {isEditing ? (
                        <Button icon={<UploadOutlined />}>Upload Photo</Button>
                      ) : null}
                    </Upload>
                    {fileList.length > 0 && (
                      <Space size={4}>
                        <Button
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => handleViewAttachment('return', typeof returnItem.id === 'number' ? returnItem.id : undefined, fileList[0])}
                        />
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadAttachment('return', typeof returnItem.id === 'number' ? returnItem.id : undefined, fileList[0])}
                        />
                      </Space>
                    )}
                    {isEditing && (
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteReturn(returnItem.id!)}
                      />
                    )}
                  </div>
                );
              })}
              {returns.length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No returns. Click 'Add' to add a return.
                </div>
              )}
            </Card>
          </Col>
            </Row>
          </TabPane>

          {/* Comment Tab */}
          <TabPane tab="Comment" key="comment">
            <Card title="Comments" bodyStyle={{ padding: '16px' }}>
              <div style={{ marginBottom: 16 }}>
                {comments.map((comment, index) => (
                  <div
                    key={comment.id || index}
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                      <Text>{comment.comment || '-'}</Text>
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      By {comment.user?.name || comment.creator?.name || 'Unknown'} • {formatDateTime(comment.created_at)}
                      {isEdit && isEditing && (
                        <Button
                          type="link"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteComment(comment.id!)}
                          style={{ marginLeft: 8, padding: 0 }}
                        />
                      )}
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No comments yet.
                  </div>
                )}
              </div>
              {isEdit && isEditing && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                  <TextArea
                    rows={6}
                    placeholder="Enter comment..."
                    value={commentText}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCommentText(e.target.value)}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddComment}
                    style={{ marginTop: 12, width: '100%' }}
                    disabled={!commentText.trim()}
                  >
                    Add Comment
                  </Button>
                </div>
              )}
            </Card>
          </TabPane>

          {/* Review Tab */}
          <TabPane tab="Review" key="review">
            <Card title="Reviews" bodyStyle={{ padding: '16px' }}>
              {/* Staff Reviews */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Staff Reviews</div>
                <div style={{ marginBottom: 16 }}>
                  {reviews.filter((review: DispatchReview) => !review.customer_id).map((review, index) => (
                    <div
                      key={review.id || index}
                      style={{
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Text>{review.review || '-'}</Text>
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        By {review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                        {isEdit && isEditing && (
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteReview(review.id!)}
                            style={{ marginLeft: 8, padding: 0 }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {reviews.filter((review: DispatchReview) => !review.customer_id).length === 0 && (
                    <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                      No staff reviews yet.
                    </div>
                  )}
                </div>
                {isEdit && isEditing && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <TextArea
                      rows={6}
                      placeholder="Enter review..."
                      value={reviewText}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReviewText(e.target.value)}
                    />
                    <Button
                      type="primary"
                      onClick={handleAddReview}
                      style={{ marginTop: 12, width: '100%' }}
                      disabled={!reviewText.trim()}
                    >
                      Add Review
                    </Button>
                  </div>
                )}
              </div>

              {/* Customer Reviews */}
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 24 }}>
                <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '16px' }}>Customer Reviews</div>
                {reviews.filter((review: DispatchReview) => review.customer_id).length > 0 ? (
                  <div>
                    {reviews.filter((review: DispatchReview) => review.customer_id).map((review: DispatchReview) => (
                      <div
                        key={review.id}
                        style={{
                          padding: '12px 0',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <Text>{review.review || '-'}</Text>
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          By {review.customer?.name || review.creator?.name || 'Unknown'} • {formatDateTime(review.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No customer reviews submitted yet
                  </div>
                )}
              </div>
            </Card>
          </TabPane>

        </Tabs>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        
        }}>
          <Space>
          {isEditing && isEdit && (
            <Button
              onClick={() => {
                form.setFieldsValue({ status: 'Collect' });
                form.submit();
              }}
              disabled={submitting}
            >
              Collect
            </Button>
          )}
          {isEditing && isEdit && (
            <Button
              onClick={() => {
                form.setFieldsValue({ status: 'Return' });
                form.submit();
              }}
              disabled={submitting}
            >
              Return
            </Button>
          )}
          {isEditing && (
            <Button 
              onClick={() => {
                setIsEditing(false);
                // Reload dispatch data to reset form
                if (isEdit && id) {
                  loadDispatch(parseInt(id, 10));
                }
              }}
              disabled={false}
            >
              Cancel
            </Button>
          )}
          {isEditing && (
            <Button 
              type="primary" 
              onClick={() => {
                form.submit();
              }} 
              loading={submitting}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          )}
          </Space>
        </div>
      </Form>
      
      {/* Buttons outside Form to avoid disabled state */}
      {!isEditing && isEdit && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
        }}>
          <Button onClick={() => navigate('/dispatches')}>Cancel</Button>
        </div>
      )}
    </Card>
  );
};
